import React, { useEffect, useRef, useState } from "react";
import "./ModalStyles.css";
import ReactSpeedometer from "react-d3-speedometer";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import {
  FacebookIcon,
  LinkedinIcon,
  TwitterIcon,
  WhatsappIcon,
} from "react-share";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

const EmissionPopup = ({
  message,
  onClose,
  onConfirm,
  totalEmission,
  formState,
  id,
}) => {
  const [loadingMessage, setLoadingMessage] = useState("Creating...");
  const modalRef = useRef(null);
  const [showSwiper, setShowSwiper] = useState(false);
  const [isShareCardVisible, setShareCardVisible] = useState(false);
  const [shareURL, setShareURL] = useState(
    "https://ecoindex.octodosolutions.com"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const featuresSwiperRef = useRef();

  const messages = [
    "Did you know? A single tree can absorb up to 48 pounds of CO₂ per year. 🌳",
    "Fun fact: The average car emits about 4.6 metric tons of CO₂ annually. 🚗",
    "Sarcastic thought: If only carbon emissions were as light as your conscience. 😏",
    "Did you know? The carbon footprint of a banana is about 80 grams. 🍌",
    "Sarcastic thought: Your emissions are high, but at least your spirits are higher. 😅",
    "Fun fact: The carbon footprint of a Google search is about 0.2 grams. 🔍",
  ];

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    onClose();
  };

  const getEmissionMessage = () => {
    if (totalEmission >= 160) {
      return "Oops! You exceeded the carbon emissions limit.";
    } else if (totalEmission >= 80) {
      return "You are within the carbon emissions limit.";
    } else {
      return "Great! You are below the carbon emission limit.";
    }
  };

  const getEmissionColor = () => {
    if (totalEmission >= 160) {
      return "#f00018";
    } else if (totalEmission >= 80) {
      return "#ffa342";
    } else {
      return "#4dbce8";
    }
  };

  const generateCertificate = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      setShowSwiper(true);
      const apiUrl =
        "https://api.ecoindex.ai/api/Individual/GenerateCertificate";
      const requestBody = {
        EventName: formState?.EventName || "Unknown Event",
        TotalAccommodationEmission: formState?.TotalAccommodationEmission,
        TotalTravelEmission: formState?.TotalTravelEmission,
        TotalEmission: totalEmission || 0,
        EventLocation: formState?.EventLocation,
        UserName: formState?.UserName,
        TotalMealEmission: formState?.TotalMealEmission,
      };
      console.log("requestbody", requestBody);
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error("Failed to generate certificate");

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error generating certificate:", error);
      setErrorMessage("Failed to generate certificate. Please try again.");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const toggleShareCard = async () => {
    const certificateData = await generateCertificate();
    if (certificateData) {
      const shareableURL = `https://api.ecoindex.ai/api/Individual/share/certificate/${certificateData.certificateId}`;
      setShareURL(shareableURL);
      setShareCardVisible((prev) => !prev);
    }
  };
  const loadingWords = [
    "Creating...",
    "Preparing...",
    "Loading...",
    "Processing...",
    "Finalizing...",
    "Crafting...",
    "Building...",
    "Designing...",
    "Assembling...",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingMessage((prevMessage) => {
        const currentIndex = loadingWords.indexOf(prevMessage);
        const nextIndex = (currentIndex + 1) % loadingWords.length;
        return loadingWords[nextIndex];
      });
    }, 4000); // Change word every 2 seconds

    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const shareCard = document.querySelector(".share-card");
      const shareButton = document.querySelector(".emission-share-button");

      if (
        shareCard &&
        !shareCard.contains(event.target) &&
        shareButton &&
        !shareButton.contains(event.target)
      ) {
        setShareCardVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="attendee-modal-overlay">
      <div
        className="attendee-modal slit-in-vertical fade show d-block"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-title"
        aria-describedby="alert-description"
        tabIndex="-1"
        ref={modalRef}
      >
        <div className="attendee-modal-dialog" role="document">
          <div className="attendee-modal-content">
            <div className="attendee-modal-header">
              <h5 className="attendee-modal-title" id="alert-title">
                <div className="popup-message">
                  {typeof message === "string" ? message : message}
                </div>
              </h5>
              <button
                type="button"
                className="attendee-close-button"
                onClick={onClose}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="attendee-modal-body" id="alert-description">
              <div className="for-flex">
                <ReactSpeedometer
                  width={220}
                  height={120}
                  needleTransitionDuration={4000}
                  needleColor="black"
                  value={Math.min(totalEmission || 0, 240)}
                  minValue={0}
                  maxValue={240}
                  segments={3}
                  segmentColors={["#4dbce8", "#ffa342", "#f00018"]}
                  customSegmentStops={[0, 80, 160, 240]}
                  forceRender={true}
                  labelFontSize={"0px"}
                  ringWidth={40}
                  valueTextFontSize={"0px"}
                />
                <div>
                  {totalEmission !== null && (
                    <div className="emission-info">
                      <p className="emission-meter">
                        <strong>TOTAL EMISSION:</strong>
                      </p>
                      <div
                        className="emission-value"
                        style={{ color: getEmissionColor(), fontSize: "20px" }}
                      >
                        {totalEmission
                          ? `${totalEmission} kg CO2`
                          : "Calculating..."}
                      </div>
                      <div
                        className="emission-level"
                        style={{ color: getEmissionColor() }}
                      >
                        {getEmissionMessage()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {errorMessage && (
                <div
                  className={`certificate-message ${
                    errorMessage.includes("Sarcastic") ? "sarcastic" : ""
                  }`}
                  data-emoji={errorMessage.match(/[🌳🚗😏🍌😅🔍]/)?.[0] || ""}
                >
                  {errorMessage.replace(/[🌳🚗😏🍌😅🔍]/g, "")}
                </div>
              )}
            </div>
            {showSwiper && (
              <div>
                <Swiper
                  grabCursor={true}
                  centeredSlides={true}
                  spaceBetween={30}
                  loop={true}
                  slidesPerView={1}
                  pagination={{ el: ".feature-pagination", clickable: true }}
                  autoplay={{
                    delay: 4000,
                    disableOnInteraction: false,
                  }}
                  onBeforeInit={(swiper) => {
                    featuresSwiperRef.current = swiper;
                  }}
                  modules={[Autoplay, Navigation, Pagination]}
                  className="emission-swiper-container"
                >
                  {/* <div className="resources-grid"> */}
                  {/* First Resource Card - PDF Download */}
                  {messages.map((message, index) => (
                    <SwiperSlide key={index}>
                      {({ isActive }) => (
                        <div
                          className={`emission-swiper-card ${
                            isActive ? "" : "inactive_feature_card"
                          }`}
                        >
                          <h3 className="emission-swiper-description">
                            {message}
                          </h3>
                        </div>
                      )}
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div className="feature-controller">
                  <div
                    className="emission-navigation"
                    onClick={() => featuresSwiperRef.current?.slidePrev()}
                  >
                    <KeyboardArrowLeftIcon fontSize="large" />
                  </div>
                  <div className="pagination-container">
                    <div className="feature-pagination"></div>
                  </div>
                  <div
                    className="emission-navigation"
                    onClick={() => featuresSwiperRef.current?.slideNext()}
                  >
                    <KeyboardArrowRightIcon fontSize="large" />
                  </div>
                </div>
              </div>
            )}
            <div className="attendee-modal-footer">
              <button
                type="button"
                className="ok-button"
                onClick={handleConfirm}
              >
                OK
              </button>
              <div className="share-container">
                <button
                  className="emission-share-button"
                  onClick={toggleShareCard}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="certificate-loading">
                      <div className="spinner"></div>
                      <span>{loadingMessage}</span>
                    </div>
                  ) : (
                    <>
                      <i className="fa fa-share-alt"></i>
                      <span> Share</span>
                    </>
                  )}
                </button>

                {isShareCardVisible && (
                  <div className="share-card">
                    <FacebookShareButton
                      url={shareURL}
                      quote="Check out my emissions summary!"
                    >
                      <FacebookIcon size={32} round={true} />
                    </FacebookShareButton>

                    <LinkedinShareButton
                      url={shareURL}
                      title="My Emissions Summary"
                      summary={`Check out this link: ${shareURL}`}
                    >
                      <LinkedinIcon size={32} round={true} />
                    </LinkedinShareButton>

                    <TwitterShareButton
                      url={shareURL}
                      title="My Emissions Summary"
                    >
                      <TwitterIcon size={32} round={true} />
                    </TwitterShareButton>

                    <WhatsappShareButton
                      url={shareURL}
                      title="My Emissions Summary"
                    >
                      <WhatsappIcon size={32} round={true} />
                    </WhatsappShareButton>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmissionPopup;
